import { apiFetch } from './client';
import type { AuthUser, TenantScope, Role, ImpersonatingDescriptor } from '@/lib/auth-store';
import { useAuth } from '@/lib/auth-store';

export async function login(email: string, password: string, mfaCode?: string) {
  return apiFetch<{
    access_token?: string;
    expires_in?: number;
    user?: { must_change_password: boolean };
    mfa_required?: boolean;
    challenge_token?: string;
  }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password, mfa_code: mfaCode }),
  });
}

export async function mfaVerify(challengeToken: string, code: string) {
  return apiFetch<{ access_token: string; expires_in: number }>('/auth/mfa/verify', {
    method: 'POST',
    body: JSON.stringify({ challenge_token: challengeToken, code }),
  });
}

export async function logout() {
  return apiFetch<{ ok: true }>('/auth/logout', { method: 'POST' });
}

export type Language = 'en' | 'he' | 'yi';

export async function fetchMe() {
  return apiFetch<{ user: AuthUser; tenant: TenantScope; language: Language }>('/auth/me');
}

export async function changePassword(currentPassword: string, newPassword: string) {
  return apiFetch<{ ok: true }>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
}

export async function patchMe(body: { displayName?: string | null; email?: string }) {
  return apiFetch<{ user: AuthUser; tenant: TenantScope; language: Language }>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/**
 * Start masquerading as another user. Swaps the in-memory access token (the
 * server returned one that already embeds `impersonated_by = <me>`) and
 * pins the "impersonating" + "originalUser" descriptors into the auth store
 * so banners and nav can react. Caller is expected to refetch `/auth/me` or
 * navigate to a neutral route afterward.
 *
 * We DO NOT touch the refresh cookie — the server deliberately left it bound
 * to the original user so a silent refresh (or the explicit stop endpoint)
 * can reconstitute the original session.
 */
export async function masquerade(userId: string): Promise<{
  impersonating: ImpersonatingDescriptor;
}> {
  const res = await apiFetch<{
    access_token: string;
    expires_in: number;
    impersonating: { id: string; email: string; role: Role };
  }>(`/auth/masquerade/${userId}`, { method: 'POST' });
  const prev = useAuth.getState().user;
  useAuth.setState({
    accessToken: res.access_token,
    impersonating: res.impersonating,
    originalUser: prev ? { email: prev.email, role: prev.role } : null,
  });
  return { impersonating: res.impersonating };
}

/**
 * Stop masquerading and return to the original session. The server reads the
 * `impersonated_by` claim on the current access token, mints a fresh token
 * for the original user, and writes `masquerade.stop` to the audit log.
 *
 * The frontend clears `impersonating` + `originalUser` and stores the new
 * access token. `user` and `tenant` are NOT updated here — the caller should
 * refetch `/auth/me` (typically by navigating; the router-level `useMe`
 * hook will pick up the new identity) to avoid drift.
 */
export async function stopMasquerading(): Promise<void> {
  const res = await apiFetch<{ access_token: string; expires_in: number }>(
    '/auth/stop-masquerade',
    { method: 'POST' },
  );
  useAuth.setState({
    accessToken: res.access_token,
    impersonating: null,
    originalUser: null,
  });
}
